import { ROUTES } from '../../lib/routes';
import { navigate } from '../../router';
import { el } from '../../utils/dom';
import { showToast } from '../../components/toast';
import {
  createAdminResource,
  deleteAdminResource,
  listAdminResources,
  patchAdminResourcePin,
  patchAdminResourcePublish,
  updateAdminResource,
  type AdminResourceCategory,
  type AdminResourcePost,
  type AdminResourcePostInput,
} from '../../api/ev123ResourcesAdmin';
import { resourceCategoryBadgeClasses, resourceCategoryLabel } from '../../data/resourcesBoard';
import { ensureAdminOrLeave } from './manageGuard';
import { buildForm, type FieldDef } from './manageForm';

const CATEGORY_OPTS: { value: AdminResourceCategory; label: string }[] = [
  { value: 'notice', label: '공지사항' },
  { value: 'law', label: '법령정보' },
  { value: 'docs', label: '업무자료실' },
  { value: 'news', label: '관련뉴스' },
];

const FILTER_OPTS: { value: 'all' | AdminResourceCategory; label: string }[] = [
  { value: 'all', label: '전체' },
  ...CATEGORY_OPTS,
];

const FIELDS: FieldDef[] = [
  { kind: 'select', key: 'category', label: '카테고리', options: CATEGORY_OPTS },
  { kind: 'text', key: 'title', label: '제목', required: true, placeholder: '게시글 제목' },
  { kind: 'textarea', key: 'summary', label: '요약', rows: 2, placeholder: '목록에 노출될 한 줄 요약' },
  {
    kind: 'textarea',
    key: 'body',
    label: '본문',
    rows: 8,
    required: true,
    placeholder: '게시 본문(필수)',
    hint: '단일 텍스트로 저장됩니다. 줄바꿈은 그대로 유지됩니다.',
  },
  { kind: 'text', key: 'source_label', label: '출처 표시명', placeholder: '예: 환경부 공고' },
  { kind: 'text', key: 'source_url', label: '출처 URL', placeholder: 'https://…' },
  { kind: 'text', key: 'attachment_label', label: '첨부 표시명', placeholder: '예: 신청서 양식 PDF' },
  { kind: 'text', key: 'attachment_url', label: '첨부 URL', placeholder: 'https://…' },
  { kind: 'number', key: 'sort_order', label: '정렬 순서(작을수록 위)' },
  { kind: 'checkbox', key: 'is_published', label: '게시(공개)' },
  { kind: 'checkbox', key: 'is_pinned', label: '상단고정' },
];

function toInput(values: Record<string, string | number | boolean>): AdminResourcePostInput {
  return {
    category: String(values.category || 'notice') as AdminResourceCategory,
    title: String(values.title || '').trim(),
    summary: String(values.summary || ''),
    body: String(values.body || ''),
    source_label: String(values.source_label || ''),
    source_url: String(values.source_url || ''),
    attachment_label: String(values.attachment_label || ''),
    attachment_url: String(values.attachment_url || ''),
    is_published: values.is_published === true,
    is_pinned: values.is_pinned === true,
    sort_order: Number(values.sort_order) || 0,
  };
}

function fromRow(row: AdminResourcePost): Record<string, string | number | boolean> {
  return {
    category: row.category || 'notice',
    title: row.title || '',
    summary: row.summary || '',
    body: row.body || '',
    source_label: row.source_label || '',
    source_url: row.source_url || '',
    attachment_label: row.attachment_label || '',
    attachment_url: row.attachment_url || '',
    is_published: !!row.is_published,
    is_pinned: !!row.is_pinned,
    sort_order: row.sort_order ?? 0,
  };
}

export function renderManageResourcesPage(root: HTMLElement): void {
  void ensureAdminOrLeave();

  let rows: AdminResourcePost[] = [];
  let filterCategory: 'all' | AdminResourceCategory = 'all';
  let searchQuery = '';
  let editingId: number | null = null;
  let busy = false;

  const wrap = el('div', { className: 'ev123-page-manage' });
  wrap.append(
    el('div', { className: 'ev123-page-head ev123-page-head--archive' }, [
      el('div', { className: 'ev123-container' }, [
        el('p', { className: 'ev123-portal-eyebrow', text: '관리자 모드' }),
        el('h1', { className: 'ev123-page-head__title', text: '자료실 관리' }),
        el('p', {
          className: 'ev123-page-head__lead',
          text: '게시글 등록·수정·삭제와 게시 여부, 상단고정을 관리합니다.',
        }),
      ]),
    ]),
  );

  const container = el('div', { className: 'ev123-container ev123-stack' });

  const back = el('a', {
    className: 'ev123-link ev123-link--back',
    href: ROUTES.manage,
    'data-nav': '',
    text: '← 관리자 대시보드',
  });
  container.appendChild(back);

  const toolbar = el('div', { className: 'ev123-manage-toolbar' });

  const catSelect = el('select', {
    className: 'ev123-input ev123-manage-toolbar__select',
    'aria-label': '카테고리 필터',
  }) as HTMLSelectElement;
  for (const o of FILTER_OPTS) {
    const opt = document.createElement('option');
    opt.value = o.value;
    opt.textContent = o.label;
    catSelect.appendChild(opt);
  }
  catSelect.addEventListener('change', () => {
    filterCategory = (catSelect.value as 'all' | AdminResourceCategory) || 'all';
    void reload();
  });

  const search = el('input', {
    className: 'ev123-input ev123-manage-toolbar__search',
    type: 'search',
    placeholder: '제목·본문 검색',
    'aria-label': '자료실 관리 검색',
  }) as HTMLInputElement;
  let searchTimer: number | null = null;
  search.addEventListener('input', () => {
    if (searchTimer) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      searchQuery = search.value.trim();
      void reload();
    }, 250);
  });

  const reloadBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
    text: '새로고침',
  }) as HTMLButtonElement;
  reloadBtn.addEventListener('click', () => void reload());

  const newBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--primary ev123-btn--sm',
    text: '+ 신규 등록',
  }) as HTMLButtonElement;
  newBtn.addEventListener('click', () => openEditor(null));

  toolbar.append(catSelect, search, reloadBtn, newBtn);

  const editorPanel = el('div', { className: 'ev123-card ev123-manage-editor', style: 'display:none' });
  const editorTitle = el('h2', { className: 'ev123-card__title', text: '새 자료 등록' });
  const form = buildForm(FIELDS);
  const editorActions = el('div', { className: 'ev123-manage-editor__actions' });
  const cancelBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
    text: '취소',
  }) as HTMLButtonElement;
  cancelBtn.addEventListener('click', () => closeEditor());
  const saveBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--primary ev123-btn--sm',
    text: '저장',
  }) as HTMLButtonElement;
  saveBtn.addEventListener('click', () => void save());
  editorActions.append(cancelBtn, saveBtn);
  editorPanel.append(editorTitle, form.element, editorActions);

  const listCard = el('div', { className: 'ev123-card ev123-card--resource-list' });
  const listBody = el('div', { className: 'ev123-manage-list' });
  listCard.appendChild(listBody);

  container.append(toolbar, editorPanel, listCard);
  wrap.appendChild(container);
  root.appendChild(wrap);

  void reload();

  function openEditor(row: AdminResourcePost | null): void {
    editingId = row ? row.id : null;
    editorTitle.textContent = row ? `자료 수정 #${row.id}` : '새 자료 등록';
    if (row) form.setValues(fromRow(row));
    else form.reset();
    editorPanel.style.display = '';
    editorPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function closeEditor(): void {
    editingId = null;
    editorPanel.style.display = 'none';
    form.reset();
  }

  async function save(): Promise<void> {
    if (busy) return;
    const values = form.values();
    const payload = toInput(values);
    if (!payload.title) {
      showToast('제목을 입력해 주세요.');
      return;
    }
    if (!payload.body) {
      showToast('본문을 입력해 주세요.');
      return;
    }
    busy = true;
    saveBtn.disabled = true;
    saveBtn.textContent = '저장 중…';
    try {
      const r = editingId
        ? await updateAdminResource(editingId, payload)
        : await createAdminResource(payload);
      if (!r.ok) {
        showToast(r.message || '저장에 실패했습니다.');
        if (r.status === 401 || r.status === 403) {
          navigate(ROUTES.homePublic);
          return;
        }
      } else {
        showToast(editingId ? '수정되었습니다.' : '등록되었습니다.');
        closeEditor();
        await reload();
      }
    } finally {
      busy = false;
      saveBtn.disabled = false;
      saveBtn.textContent = '저장';
    }
  }

  async function reload(): Promise<void> {
    listBody.replaceChildren(
      el('p', { className: 'ev123-card__desc', style: 'margin:1rem 0', text: '불러오는 중…' }),
    );
    const r = await listAdminResources({ category: filterCategory, q: searchQuery });
    if (!r.ok) {
      listBody.replaceChildren(
        el('p', {
          className: 'ev123-card__desc',
          style: 'margin:1rem 0',
          text: r.message || '목록을 불러오지 못했습니다.',
        }),
      );
      if (r.status === 401 || r.status === 403) {
        navigate(ROUTES.homePublic);
      }
      return;
    }
    rows = r.data;
    renderRows();
  }

  function renderRows(): void {
    listBody.replaceChildren();
    if (rows.length === 0) {
      listBody.appendChild(
        el('p', {
          className: 'ev123-card__desc',
          style: 'margin:1rem 0',
          text: '등록된 자료가 없습니다. “신규 등록”으로 추가해 주세요.',
        }),
      );
      return;
    }
    for (const row of rows) listBody.appendChild(renderRow(row));
  }

  function renderRow(row: AdminResourcePost): HTMLElement {
    const item = el('div', { className: 'ev123-manage-row' });
    const head = el('div', { className: 'ev123-manage-row__head' });

    const badges = el('div', { className: 'ev123-manage-row__badges' });
    badges.appendChild(
      el('span', {
        className: resourceCategoryBadgeClasses(
          (['notice', 'law', 'docs', 'news'].includes(row.category)
            ? row.category
            : 'notice') as AdminResourceCategory,
        ),
        text: resourceCategoryLabel(
          (['notice', 'law', 'docs', 'news'].includes(row.category)
            ? row.category
            : 'notice') as AdminResourceCategory,
        ),
      }),
    );
    if (row.is_pinned) badges.appendChild(el('span', { className: 'ev123-badge ev123-badge--pinned', text: '상단고정' }));
    badges.appendChild(
      el('span', {
        className: row.is_published
          ? 'ev123-badge ev123-badge--published'
          : 'ev123-badge ev123-badge--draft',
        text: row.is_published ? '게시중' : '비게시',
      }),
    );

    const titleBox = el('div', { className: 'ev123-manage-row__title-box' });
    titleBox.append(
      el('p', { className: 'ev123-manage-row__title', text: row.title || '(제목 없음)' }),
      el('p', {
        className: 'ev123-manage-row__meta',
        text: `#${row.id} · 정렬 ${row.sort_order ?? 0}${row.updated_at ? ` · 수정 ${row.updated_at.slice(0, 10)}` : ''}`,
      }),
    );

    head.append(badges, titleBox);

    if (row.summary) {
      titleBox.appendChild(el('p', { className: 'ev123-manage-row__summary', text: row.summary }));
    }

    const actions = el('div', { className: 'ev123-manage-row__actions' });

    const togglePub = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
      text: row.is_published ? '비게시 전환' : '게시 전환',
    }) as HTMLButtonElement;
    togglePub.addEventListener('click', () => void onTogglePublish(row, togglePub));

    const togglePin = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
      text: row.is_pinned ? '고정 해제' : '상단 고정',
    }) as HTMLButtonElement;
    togglePin.addEventListener('click', () => void onTogglePin(row, togglePin));

    const editBtn = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--secondary ev123-btn--sm',
      text: '수정',
    }) as HTMLButtonElement;
    editBtn.addEventListener('click', () => openEditor(row));

    const delBtn = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--danger ev123-btn--sm',
      text: '삭제',
    }) as HTMLButtonElement;
    delBtn.addEventListener('click', () => void onDelete(row, delBtn));

    actions.append(togglePub, togglePin, editBtn, delBtn);

    item.append(head, actions);
    return item;
  }

  async function onTogglePublish(row: AdminResourcePost, btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    const r = await patchAdminResourcePublish(row.id, !row.is_published);
    btn.disabled = false;
    if (!r.ok) {
      showToast(r.message || '상태 변경에 실패했습니다.');
      if (r.status === 401 || r.status === 403) navigate(ROUTES.homePublic);
      return;
    }
    showToast(r.data.is_published ? '게시되었습니다.' : '비게시 처리했습니다.');
    await reload();
  }

  async function onTogglePin(row: AdminResourcePost, btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    const r = await patchAdminResourcePin(row.id, !row.is_pinned);
    btn.disabled = false;
    if (!r.ok) {
      showToast(r.message || '상태 변경에 실패했습니다.');
      if (r.status === 401 || r.status === 403) navigate(ROUTES.homePublic);
      return;
    }
    showToast(r.data.is_pinned ? '상단고정 처리했습니다.' : '상단고정을 해제했습니다.');
    await reload();
  }

  async function onDelete(row: AdminResourcePost, btn: HTMLButtonElement): Promise<void> {
    const ok = window.confirm(`정말 삭제할까요?\n#${row.id} ${row.title || ''}`);
    if (!ok) return;
    btn.disabled = true;
    const r = await deleteAdminResource(row.id);
    btn.disabled = false;
    if (!r.ok) {
      showToast(r.message || '삭제에 실패했습니다.');
      if (r.status === 401 || r.status === 403) navigate(ROUTES.homePublic);
      return;
    }
    showToast('삭제되었습니다.');
    await reload();
  }
}
